import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer, ArrowLeft } from "@phosphor-icons/react";

// Hourly slots between shift_start and shift_end (inclusive of last full hour).
// Handles any window from 06:00 to 22:00, any duration.
const hoursBetween = (start, end) => {
  const toM = (v) => { const [h, m] = (v || "").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
  const s = toM(start); const e = toM(end);
  if (!s || !e || e <= s) return [];
  const out = [];
  // Round start down to the hour so the sheet is easy to read.
  for (let m = Math.floor(s / 60) * 60; m + 60 <= Math.ceil(e / 60) * 60; m += 60) {
    const hh = Math.floor(m / 60);
    out.push(`${String(hh).padStart(2, "0")}:00`);
  }
  return out;
};
const hourLabel = (h) => {
  const [hh] = h.split(":").map(Number);
  const s = hh > 12 ? `${hh - 12} PM` : (hh === 12 ? "12 PM" : (hh === 0 ? "12 AM" : `${hh} AM`));
  const next = (hh + 1) % 24;
  const e = next > 12 ? `${next - 12} PM` : (next === 12 ? "12 PM" : (next === 0 ? "12 AM" : `${next} AM`));
  return `${s} – ${e}`;
};

export default function JobCard() {
  const [params, setParams] = useSearchParams();
  const singleDate = params.get("date");
  const fromParam = params.get("from");
  const toParam = params.get("to");
  const staffFilter = params.get("staff_id") || "";
  // Resolve which window to fetch. Single `date` overrides range.
  const useRange = !singleDate && (fromParam || toParam);
  const rangeFrom = fromParam || toParam || "";
  const rangeTo = toParam || fromParam || "";
  const displayDate = singleDate || (useRange ? (rangeFrom === rangeTo ? rangeFrom : `${rangeFrom} → ${rangeTo}`) : new Date().toISOString().slice(0, 10));

  const [schedules, setSchedules] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [settings, setSettings] = useState({ company_name: "US Operations" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const qs = new URLSearchParams();
      if (singleDate) {
        qs.set("date", singleDate);
      } else if (useRange) {
        if (rangeFrom) qs.set("date_from", rangeFrom);
        if (rangeTo) qs.set("date_to", rangeTo);
      } else {
        qs.set("date", new Date().toISOString().slice(0, 10));
      }
      if (staffFilter) qs.set("staff_id", staffFilter);
      const [sch, s, st] = await Promise.all([
        api.get(`/schedules?${qs.toString()}`),
        api.get("/settings"),
        api.get("/staff"),
      ]);
      // Sort: date asc, then by staff name for a predictable print order.
      const sorted = [...sch.data].sort((a, b) => (a.date === b.date ? (a.staff_name || "").localeCompare(b.staff_name || "") : a.date.localeCompare(b.date)));
      setSchedules(sorted);
      setSettings(s.data);
      setStaffList(st.data.filter((x) => x.active));
      setLoading(false);
    })();
  }, [singleDate, rangeFrom, rangeTo, staffFilter, useRange]);

  const setStaff = (sid) => {
    const next = new URLSearchParams(params);
    if (sid) next.set("staff_id", sid); else next.delete("staff_id");
    setParams(next, { replace: true });
  };

  const cards = useMemo(() => schedules, [schedules]);

  if (loading) return <div className="p-8 font-display uppercase">Loading...</div>;

  if (cards.length === 0) {
    return (
      <div className="p-8" data-testid="jobcard-empty">
        <div className="brut-card p-6">
          <h2 className="font-display text-2xl uppercase">No job cards for {displayDate}</h2>
          <p className="text-sm mt-2 text-slate-600">No staff scheduled in this window. Go to Schedule and assign operations first.</p>
          <Link to="/schedule" className="brut-btn px-3 py-2 mt-3 inline-block">Back to Schedule</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="jobcard-page">
      <style>{`
        @page { size: 8.5in 11in; margin: 0; }
        @media print {
          body { background: #fff !important; }
          .card-wrap { margin: 0 !important; padding: 0 !important; }
          .jobcard { box-shadow: none !important; page-break-inside: avoid; }
          .toolbar { display: none !important; }
        }
        /* Each card is 5.5in tall; two stack naturally on a US-Letter 8.5x11 sheet. */
        .jobcard {
          width: 8.5in; height: 5.5in;
          padding: 0.28in 0.3in;
          background: #fff;
          color: #0f172a;
          font-family: 'IBM Plex Mono', monospace;
          box-sizing: border-box;
          border: 2px solid #0f172a;
          border-bottom-style: dashed; /* visual cue for cut-line between the two cards */
          display: flex; flex-direction: column;
          margin: 0 auto;
        }
        .jobcard:nth-child(even) { border-bottom-style: solid; }
        @media screen {
          .jobcard { margin: 12px auto; border-bottom-style: solid; }
        }
      `}</style>

      <div className="flex items-center justify-between p-4 toolbar flex-wrap gap-2">
        <Link to="/schedule" className="brut-tag brut-tag-white inline-flex items-center gap-1">
          <ArrowLeft size={10} weight="bold" /> Back to Schedule
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-mono-ui text-xs">
            {cards.length} card{cards.length === 1 ? "" : "s"} · {displayDate} · <b>{Math.ceil(cards.length / 2)}</b> sheet{Math.ceil(cards.length / 2) === 1 ? "" : "s"} of 8.5×11
          </div>
          <select
            value={staffFilter}
            onChange={(e) => setStaff(e.target.value)}
            className="brut-input py-1 text-xs"
            data-testid="jobcard-staff-filter"
          >
            <option value="">All staff</option>
            {staffList.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <button onClick={() => window.print()} className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="print-jobcards-btn">
          <Printer size={14} weight="bold" /> Print / Save PDF
        </button>
      </div>

      <div className="card-wrap py-4">
        {cards.map((c) => {
          // Multi-op safe: operations[] from backend, fallback to legacy single fields
          const ops = (c.operations && c.operations.length > 0)
            ? c.operations
            : [{ code: c.operation_code || "—", name: c.operation_name || "" }];
          return (
          <div key={c.id} className="jobcard" data-testid={`jobcard-${c.staff_name}`}>
            {/* Header — company + big DATE */}
            <div style={{ borderBottom: "2px solid #0f172a", paddingBottom: 6, display: "flex", alignItems: "center", gap: 10 }}>
              <img src="/logo-gate.jpg" alt="GATE" style={{ width: 38, height: 38, border: "1.5px solid #334155", objectFit: "cover" }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 10, letterSpacing: 2, color: "#1d4ed8" }}>// {settings.company_name?.toUpperCase()}</div>
                <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 20, lineHeight: 1, marginTop: 2 }}>DAILY JOB CARD</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: "#64748b" }}>Date</div>
                <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 26, lineHeight: 1, color: "#0f172a" }}>{c.date}</div>
              </div>
            </div>

            {/* Operator (BIG) + Operation(s) (BIG) + Shift (BIG) */}
            <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1.2fr 1.6fr 0.9fr", gap: 8, fontSize: 11 }}>
              <div style={{ border: "2px solid #0f172a", padding: 8, background: "#fef3c7" }}>
                <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: "#78350f" }}>Operator</div>
                <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 22, lineHeight: 1.05, marginTop: 2 }}>{c.staff_name}</div>
                <div style={{ fontSize: 10, color: "#334155", marginTop: 2 }}>{c.staff_designation}</div>
              </div>
              <div style={{ border: "2px solid #0f172a", padding: 8, background: (ops[0]?.color ? `${ops[0].color}22` : "#dbeafe"), borderLeftWidth: 8, borderLeftColor: ops[0]?.color || "#1e3a8a" }}>
                <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: "#1e3a8a" }}>Operation{ops.length > 1 ? "s" : ""}</div>
                {ops.map((op, oi) => (
                  <div key={oi} style={{ marginTop: oi === 0 ? 2 : 4, borderTop: oi > 0 ? "1px dashed #1e3a8a" : "none", paddingTop: oi > 0 ? 3 : 0 }}>
                    <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: ops.length > 1 ? 16 : 20, lineHeight: 1, color: op.color || "#0f172a" }}>{op.code}</div>
                    <div style={{ fontSize: ops.length > 1 ? 10 : 11 }}>{op.name}</div>
                  </div>
                ))}
                {ops.length > 1 && (
                  <div style={{ fontSize: 8.5, color: "#1e3a8a", marginTop: 3, fontStyle: "italic" }}>Worker may switch between the operations during the shift.</div>
                )}
              </div>
              <div style={{ border: "2px solid #0f172a", padding: 8, background: "#dcfce7" }}>
                <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: "#166534" }}>Shift</div>
                <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 20, lineHeight: 1, marginTop: 2 }}>{c.shift_start}</div>
                <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 20, lineHeight: 1 }}>–{c.shift_end}</div>
                {c.assembly_run_number && (
                  <div style={{ fontSize: 9, marginTop: 3, color: "#166534" }}>Run {c.assembly_run_number}</div>
                )}
              </div>
            </div>

            {/* Time tracking + Day-end totals side-by-side */}
            <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1.3fr", gap: 10, fontSize: 10, flex: 1 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <TimeField label="In Time" />
                <TimeField label="Out Time" />
                <TimeField label="Break Start" />
                <TimeField label="Break End" />
                <div style={{ marginTop: "auto", borderTop: "1px dashed #64748b", paddingTop: 4, fontSize: 9 }}>
                  Notes / Issues:
                  <div style={{ height: 30, borderBottom: "1px dotted #64748b" }} />
                  <div style={{ height: 30, borderBottom: "1px dotted #64748b" }} />
                </div>
              </div>
              <div>
                <div style={{ fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", color: "#64748b", marginBottom: 4 }}>Day-End Totals — one number per operation</div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, border: "2px solid #0f172a" }}>
                  <thead>
                    <tr style={{ background: "#f1f5f9" }}>
                      <th style={{ border: "1.5px solid #0f172a", padding: "6px 6px", textAlign: "left", width: "58%", fontFamily: "Archivo Black, sans-serif", fontSize: 11 }}>Operation</th>
                      <th style={{ border: "1.5px solid #0f172a", padding: "6px 6px", textAlign: "right", fontFamily: "Archivo Black, sans-serif", fontSize: 11 }}>Qty Produced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ops.map((op, oi) => (
                      <tr key={oi}>
                        <td style={{ border: "1.5px solid #0f172a", padding: "10px 6px" }}>
                          <div style={{ fontFamily: "Archivo Black, sans-serif", fontSize: 15, lineHeight: 1 }}>{op.code}</div>
                          <div style={{ fontSize: 10, color: "#334155", marginTop: 2 }}>{op.name}</div>
                        </td>
                        <td style={{ border: "1.5px solid #0f172a", padding: "10px 6px", height: 42 }}></td>
                      </tr>
                    ))}
                    <tr style={{ background: "#fef3c7" }}>
                      <td style={{ border: "1.5px solid #0f172a", padding: "8px 6px", fontFamily: "Archivo Black, sans-serif", fontSize: 12 }}>TOTAL FOR THE DAY</td>
                      <td style={{ border: "1.5px solid #0f172a", padding: "8px 6px", height: 32 }}></td>
                    </tr>
                  </tbody>
                </table>
                <div style={{ fontSize: 9, color: "#64748b", marginTop: 4, fontStyle: "italic" }}>
                  Write one total per operation at the end of the day.
                </div>
              </div>
            </div>

            {/* Signature footer */}
            <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 30, paddingTop: 6, borderTop: "2px solid #1e293b", fontSize: 9 }}>
              <div>
                Operator Signature
                <div style={{ borderBottom: "1px solid #1e293b", height: 22, marginTop: 12 }} />
              </div>
              <div>
                Supervisor Signature
                <div style={{ borderBottom: "1px solid #1e293b", height: 22, marginTop: 12 }} />
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function TimeField({ label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase", color: "#64748b", width: 68 }}>{label}</div>
      <div style={{ flex: 1, borderBottom: "1.5px solid #1e293b", height: 18 }} />
    </div>
  );
}
